<template>
  <div :class="{ 'auto-form-disabled': !$can('car_edit') }">
    <v-overlay :value="isLoading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-form ref="autoForm" lazy-validation>
      <!-- Auto info -->
      <main-info
        ref="autoDetails"
        :autoDetails.sync="credentials.autoDetails"
      />
      <v-divider></v-divider>
      <doc-and-promo
        ref="docAndPromo"
        :techPassport.sync="credentials.techPassport"
        :promo.sync="credentials.promo"
      />
      <v-divider></v-divider>
      <dates ref="dates" :dates.sync="credentials.dates" />
      <v-divider></v-divider>
      <photos
        ref="photos"
        :photos.sync="credentials.photos"
        :media-comment.sync="credentials.mediaComment"
        :isEdit="isEdit"
      />
      <v-divider></v-divider>
      <additional-media
        ref="additionalMedia"
        :additional-photo.sync="credentials.additionalPhoto"
        :additional-video.sync="credentials.additionalVideo"
        :additional-photo-comment.sync="credentials.additionalPhotoComment"
        :additional-video-comment.sync="credentials.additionalVideoComment"
      />
      <v-divider></v-divider>
      <check-list ref="checkList" :checkList.sync="credentials.checkList" />
      <v-divider></v-divider>
      <technical-condition
        ref="technicalCondition"
        :technical-condition.sync="credentials.technicalCondition"
        :comments.sync="credentials.technicalConditionComment"
      />
      <v-row class="d-flex align-end flex-column">
        <v-flex xs12>
          <!-- Error Alert -->
          <alert-error class="px-3" />
          <!-- Success Alert-->
          <v-layout
            v-if="successAlert.state"
            row
            class="alert-wrapper mt-2 mb-2 px-3"
          >
            <v-flex sm12>
              <v-alert :value="successAlert.state" type="success">
                {{ successAlert.message }}
              </v-alert>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-row>
          <div v-if="isEdit"></div>
          <v-btn
            v-if="$can('car_edit')"
            type="button"
            :disabled="areBtnsDisabled"
            class="warning ma-2 mr-5"
            depressed
            large
            @click="saveAuto"
            >{{ $t('common.btns.save') }}</v-btn
          >
        </v-row>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { mask } from 'vue-the-mask';
import DateHelper from '@/helpers/dateHelper';
import MainInfo from '@/components/auto/AutoForm/MainInfo.vue';
import DocAndPromo from '@/components/auto/AutoForm/DocAndPromo.vue';
import Dates from '@/components/auto/AutoForm/Dates.vue';
import Photos from '@/components/auto/AutoForm/Photos.vue';
import CheckList from '@/components/auto/AutoForm/CheckList.vue';
import AlertError from '@/components/shared/AlertError.vue';
import TechnicalCondition from '@/components/auto/AutoForm/TechnicalCondition.vue';
import AdditionalMedia from '@/components/auto/AutoForm/AdditionalMedia.vue';

export default {
  name: 'AddAutoForm',
  directives: {
    mask,
  },
  components: {
    AlertError,
    MainInfo,
    DocAndPromo,
    Dates,
    Photos,
    CheckList,
    TechnicalCondition,
    AdditionalMedia,
  },
  data() {
    return {
      isLoading: false,
      isProcessForm: false,
      isEdit: false,
      credentials: {
        autoDetails: {},
        techPassport: {},
        promo: {},
        dates: {},
        photos: {},
        mediaComment: {},
        checkList: {},
        technicalCondition: {},
        technicalConditionComment: {},
        additionalPhoto: [],
        additionalVideo: [],
        additionalPhotoComment: '',
        additionalVideoComment: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      successAlert: 'global/getAlertSuccess',
      autoCompanies: 'auto/getAutosCompanies',
      autoInvestors: 'auto/getAutosInvestors',
      imagesUploading: 'global/getImagesUploadingLength',
      autoResponsibles: 'auto/getAutosResponsibles',
    }),
    autoId() {
      return this.$route.params.autoId;
    },
    areBtnsDisabled() {
      return this.isProcessForm || this.imagesUploading > 0;
    },
  },
  watch: {
    async autoId() {
      this.isLoading = true;
      await this.loadData();
      this.isLoading = false;
    },
  },
  beforeCreate() {
    this.$store.commit('auto/setAutoLoadingData', true);
  },
  async created() {
    this.isLoading = true;
    await Promise.all([
      this.loadData(),
      this.$store.dispatch('rates/fetchList'),
      this.$store.dispatch('weekend-schedule/fetchList'),
    ]);
    this.isLoading = false;
  },
  beforeDestroy() {
    this.setBrand({ id: null, title: null });
    this.setModel({ id: null, title: null });
  },
  methods: {
    ...mapMutations({
      clearAlertError: 'global/clearAlertError',
      clearAlertSuccess: 'global/clearAlertSuccess',
      setAlertSuccess: 'global/setAlertSuccess',
      setBrand: 'auto/setAutoFormBrand',
      setModel: 'auto/setAutoFormModel',
      setLoader: 'auto/setAutoLoadingData',
    }),
    ...mapActions({
      getProperties: 'auto/fetchAutosProperties',
      createAuto: 'auto/createAuto',
      editAuto: 'auto/editAuto',
      getAutoData: 'auto/fetchAutoData',
    }),
    async loadData() {
      await this.getProperties();
      if (this.autoId) {
        // load auto data
        try {
          const autoData = await this.getAutoData(this.autoId);
          if (autoData) {
            this.isEdit = true;
            this.prepareInputData(autoData);
          }

          this.$refs.autoForm.resetValidation();
        } catch (error) {
          if (error.status === 404) {
            this.$router.push({ name: 'not-found' });
          }
        }
      } else {
        this.setLoader(false);
      }
    },
    async saveAuto() {
      const isValidForm = this.$refs.autoForm.validate();
      const isValidPhotos = this.validatePhotos();
      if (isValidForm && isValidPhotos) {
        this.isProcessForm = true;
        this.getChildrenComponentsData();
        let response = null;
        const outData = this.prepareOutData();
        try {
          if (this.isEdit) {
            const autoData = {
              autoId: this.autoId,
              data: outData,
            };
            response = await this.editAuto(autoData);
          } else {
            response = await this.createAuto(outData);
          }
          if (response.success) {
            const successAlert = {
              state: true,
              message: this.$t('auto.saving.success'),
            };
            if (response.data.message) {
              successAlert.message = response.data.message;
            }
            this.clearAlertError();
            this.setAlertSuccess(successAlert);
            const that = this;
            setTimeout(() => {
              that.clearAlertSuccess();
              that.$router.push({ name: 'autos-list' });
            }, 2500);
          }
        } catch (error) {
          this.isProcessForm = false;
        }
      }
    },
    prepareOutData() {
      const data = { ...this.credentials };
      const datesData = this.prepareDatesComponent();
      const docData = this.prepareDocComponent();
      const promoData = this.preparePromoComponent();
      const { photos, mediaComment } = this.preparePhotosComponent();
      const checkListData = this.prepareCheckListComponent();
      const {
        technicalCondition,
        technicalConditionComment,
      } = this.prepareTechnicalConditionComponent();
      const {
        additionalPhoto,
        additionalVideo,
        additionalPhotoComment,
        additionalVideoComment,
      } = this.prepareAdditionalMediaComponent();

      const outData = {
        ...data.autoDetails,
        ...datesData,
        ...promoData,
        photoCertificateCar: docData,
        state: {
          photo: photos,
          mediaComment,
          equipment: checkListData,
          checkList: technicalCondition,
          checkListComment: technicalConditionComment,
          additionalPhoto,
          additionalVideo,
          additionalPhotoComment,
          additionalVideoComment,
        },
      };

      outData.rent_rate = {
        id: outData.rentRateId,
        date: outData.rentRateDate,
      };

      delete outData.rentRateId;
      delete outData.rentRateDate;

      if (typeof data.autoDetails.companyId === 'object') {
        outData.companyId = data.autoDetails.companyId.id;
      } else {
        outData.companyId = data.autoDetails.companyId;
      }
      if (typeof data.autoDetails.investorId === 'object') {
        outData.investorId = data.autoDetails.investorId.id;
      } else {
        outData.investorId = data.autoDetails.investorId;
      }
      if (typeof data.autoDetails.responsible_fines_id === 'object') {
        outData.responsible_fines_id = data.autoDetails.responsible_fines_id.userId;
      } else {
        outData.responsible_fines_id = data.autoDetails.userId;
      }
      outData.statusId = outData.notActive ? 2 : 1;
      delete outData.notActive;
      return outData;
    },
    prepareInputData(inputData) {
      const autoDetailsComponent = this.$refs.autoDetails;
      const mainInfo = {};

      Object.keys(autoDetailsComponent.credentials).forEach((key) => {
        if (inputData[key]) {
          mainInfo[key] = inputData[key];
        }
      });
      // find investor id from properties
      this.autoCompanies.forEach((company) => {
        if (company.id === mainInfo.companyId) {
          mainInfo.companyId = company;
        }
      });
      this.autoInvestors.forEach((investor) => {
        if (investor.id === mainInfo.investorId) {
          mainInfo.investorId = investor;
        }
      });
      this.autoResponsibles.forEach((responsible) => {
        if (responsible.userId === mainInfo.responsible_fines_id) {
          mainInfo.responsible_fines_id = responsible;
        }
      });
      mainInfo.notActive = inputData.statusId !== 1;
      mainInfo.brand = inputData.brand;
      mainInfo.model = inputData.model;
      mainInfo.transmissionType = String(inputData.transmissionType);
      mainInfo.registrationCertificate = inputData.registrationCertificate;
      mainInfo.color = inputData.color;
      mainInfo.kaskoName = inputData.kaskoName;
      mainInfo.osgpoName = inputData.osgpoName;
      mainInfo.tireSize = inputData.tireSize;
      mainInfo.weekendScheduleId = inputData.weekendScheduleId;
      autoDetailsComponent.writeDataToFields(mainInfo);

      // prepare docs and promo data
      const docAndPromoComponent = this.$refs.docAndPromo;
      const docData = {
        techPassport1: inputData.photoCertificateCar[0],
        techPassport2: inputData.photoCertificateCar[1],
      };
      const promoData = {
        isPromo: inputData.promo === 1,
        promoPhoto: inputData.photoPromo[0],
      };
      docAndPromoComponent.writeDataToFields(docData, promoData);

      // prepare dates data
      const datesComponent = this.$refs.dates;
      const datesData = {};
      Object.keys(datesComponent.credentials).forEach((key) => {
        if (inputData[key] || inputData[key] === 0) {
          if (datesComponent.dateFields.indexOf(key) >= 0) {
            datesData[key] = inputData[key];
          } else {
            datesData[key] = inputData[key];
          }
        }
      });
      datesComponent.writeDataToFields(datesData);

      // prepare photos data
      const { media, mediaComment } = this.$store.state.auto.autosState;
      const photosComponent = this.$refs.photos;
      const photosData = {};
      const commentsData = {};

      Object.keys(media).forEach((key) => {
        if (inputData.state.photo[key]) {
          photosData[key] = {
            name: inputData.state.photo[key].value,
            link: inputData.state.photo[key].link,
          };
        } else {
          photosData[key] = null;
        }
      });

      Object.keys(mediaComment).forEach((key) => {
        if (inputData.state.mediaComment && inputData.state.mediaComment[key]) {
          commentsData[key] = inputData.state.mediaComment[key].value;
        } else {
          commentsData[key] = '';
        }
      });

      photosComponent.writeDataToFields({
        photos: photosData,
        comments: commentsData,
      });

      // prepare check list data
      const checkListComponent = this.$refs.checkList;
      const { equipment } = this.$store.state.auto.autosState;
      const checkListData = {};

      Object.keys(equipment).forEach((key) => {
        if (inputData.state.equipment[key]) {
          checkListData[key] = inputData.state.equipment[key].value;
        } else {
          checkListData[key] = '0';
        }
      });
      checkListComponent.writeDataToFields(checkListData);

      const technicalConditionComponent = this.$refs.technicalCondition;
      const { checkList } = this.$store.state.auto.autosState;
      const checkListComment = inputData.state.checkListComment || {};
      const technicalConditionData = {};

      Object.keys(checkList).forEach((key) => {
        if (inputData.state.checkList[key]) {
          technicalConditionData[key] = inputData.state.checkList[key].value;
        } else {
          technicalConditionData[key] = null;
        }
      });
      technicalConditionComponent.writeDataToFields({
        data: technicalConditionData,
        comments: Object.keys(checkListComment).reduce((obj, key) => {
          obj[key] = checkListComment[key].value; // eslint-disable-line
          return obj;
        }, {}),
      });

      const additionalMediaComponent = this.$refs.additionalMedia;
      const {
        additionalPhotoComment,
        additionalVideoComment,
      } = inputData.state;

      let { additionalPhoto, additionalVideo } = inputData.state;

      additionalPhoto = additionalPhoto || [];
      additionalVideo = additionalVideo || [];

      const additionalPhotoData = additionalPhoto.map(({ link, value }) => ({
        link,
        name: value,
      }));

      const additionalVideoData = additionalVideo.map(({ link, value }) => ({
        link,
        name: value,
      }));

      additionalMediaComponent.writeDataToFields({
        additionalPhoto: additionalPhotoData,
        additionalVideo: additionalVideoData,
        additionalPhotoComment: additionalPhotoComment
          ? additionalPhotoComment.comment.value
          : '',
        additionalVideoComment: additionalVideoComment
          ? additionalVideoComment.comment.value
          : '',
      });
    },
    prepareDatesComponent() {
      const data = { ...this.credentials.dates };
      Object.keys(data).forEach((key) => {
        const val = data[key];
        const isDate = /^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.([0-9][0-9][0-9][0-9])$/.test(
          val,
        );

        if (typeof val === 'number') {
          // eslint-disable-next-line
          data[key] = val === 0 ? false : true;
        } else if (isDate) {
          data[key] = DateHelper.prepareDateToSend(val);
        } else {
          data[key] = val;
        }
      });

      return data;
    },
    prepareDocComponent() {
      const data = { ...this.credentials.techPassport };
      return [data.techPassport1.name, data.techPassport2.name];
    },
    preparePromoComponent() {
      const data = { ...this.credentials.promo };
      const outData = {
        promo: data.isPromo,
      };
      if (data.promoPhoto) {
        outData.photoPromo = [data.promoPhoto.name];
      }
      return outData;
    },
    preparePhotosComponent() {
      const photos = { ...this.credentials.photos };

      Object.keys(photos).forEach((key) => {
        if (!photos[key]) return;
        photos[key] = photos[key].name;
      });
      return {
        photos,
        mediaComment: this.credentials.mediaComment,
      };
    },
    prepareCheckListComponent() {
      const data = { ...this.credentials.checkList };
      Object.keys(data).forEach((key) => {
        // eslint-disable-next-line
        data[key] = data[key] === '0' ? false : true;
      });
      return data;
    },
    prepareTechnicalConditionComponent() {
      const {
        technicalCondition,
        technicalConditionComment,
      } = this.credentials;
      const data = {};

      Object.keys(technicalCondition).forEach((key) => {
        if (!technicalCondition[key]) return;
        // eslint-disable-next-line
        data[key] = technicalCondition[key];
      });

      return {
        technicalCondition: data,
        technicalConditionComment,
      };
    },
    prepareAdditionalMediaComponent() {
      const additionalPhoto = this.credentials.additionalPhoto.map(
        ({ name }) => name,
      );
      const additionalVideo = this.credentials.additionalVideo.map(
        ({ name }) => name,
      );
      const additionalPhotoComment = {
        comment: this.credentials.additionalPhotoComment,
      };
      const additionalVideoComment = {
        comment: this.credentials.additionalVideoComment,
      };

      return {
        additionalPhoto,
        additionalVideo,
        additionalPhotoComment,
        additionalVideoComment,
      };
    },
    validatePhotos() {
      const isValidDocs = this.$refs.docAndPromo.validatePhotos();
      const isValidPhotos = this.$refs.photos.validatePhotos();
      return isValidDocs && isValidPhotos;
    },
    getChildrenComponentsData() {
      this.$refs.autoDetails.getComponentData();
      this.$refs.docAndPromo.getComponentData();
      this.$refs.dates.getComponentData();
      this.$refs.photos.getComponentData();
      this.$refs.checkList.getComponentData();
      this.$refs.technicalCondition.getComponentData();
      this.$refs.additionalMedia.getComponentData();
    },
  },
};
</script>

<style lang="scss">
.auto-form-disabled {
  .v-input,
  .v-select,
  .clear-btn {
    pointer-events: none !important;
  }
}
</style>
